/**
 * ConfluenceLinkingContent Component
 *
 * Main content component for the Confluences tab in PlaybookLinkageTabs.
 * Displays linked confluences for the selected playbook with:
 * - Dual-list layout separated by polarity (positive/negative)
 * - Multi-select with checkboxes
 * - Move between lists (change polarity)
 * - Unlink individual confluences
 * - Add confluences via picker modal with polarity assignment
 * - Inline editing of confluence items via ConfluenceModal
 *
 * Selection behavior:
 * - Selecting in one list clears selection in the other (prevents ambiguous moves)
 * - Move operation changes polarity for selected items and clears selection
 */

import React, { useState, useCallback, useEffect } from 'react';

import { LinkedConfluencesDualList } from './LinkedConfluencesDualList';
import { ConfluenceLinkPickerModal } from './ConfluenceLinkPickerModal';
import { ConfluenceModal } from '../Confluences/ConfluenceModal';
import { useStrategiesPlaybooks } from '../context/StrategiesPlaybooksContext';
import { confluenceService, marketConditionCategoryService } from '@/services';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { Confluence } from '@/types/schema/Confluence';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { PlaybookConfluenceLink } from '@/types/schema/Playbook';

export type ConfluenceLinkingContentProps = Record<string, never>;

export const ConfluenceLinkingContent: React.FC<ConfluenceLinkingContentProps> = () => {
  const { localPlaybookConfluences, setLocalPlaybookConfluences } = useStrategiesPlaybooks();

  // Picker modal state
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Edit modal state
  const [editingConfluence, setEditingConfluence] = useState<Confluence | undefined>(undefined);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createPolarity, setCreatePolarity] = useState<'positive' | 'negative'>('negative');

  // Shared saving state for both edit and create modals
  const [isSaving, setIsSaving] = useState(false);

  // Categories for edit modal
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [allCategories, setAllCategories] = useState<MarketConditionCategory[]>([]);

  /**
   * Load categories for the edit modal
   * Called on mount and before opening edit modal to ensure fresh data
   */
  const loadCategories = useCallback(async () => {
    try {
      const allCats = await marketConditionCategoryService.getAll();
      setAllCategories(allCats);
      setCategories(allCats.filter((c) => c.status === 'active'));
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Selection state (per-list)
  const [positiveSelected, setPositiveSelected] = useState<Set<string>>(new Set());
  const [negativeSelected, setNegativeSelected] = useState<Set<string>>(new Set());

  /**
   * Handle selection change in a polarity list
   * Clears the opposite list's selection to prevent ambiguous moves
   */
  const handleSelectionChange = useCallback(
    (polarity: 'positive' | 'negative', selected: Set<string>) => {
      if (polarity === 'positive') {
        setPositiveSelected(selected);
        // Clear negative selection when selecting in positive
        if (selected.size > 0) {
          setNegativeSelected(new Set());
        }
      } else {
        setNegativeSelected(selected);
        // Clear positive selection when selecting in negative
        if (selected.size > 0) {
          setPositiveSelected(new Set());
        }
      }
    },
    []
  );

  /**
   * Handle move operation
   * Changes polarity for selected items and clears selection
   */
  const handleMove = useCallback(
    (direction: 'toPositive' | 'toNegative') => {
      const targetPolarity = direction === 'toPositive' ? 'positive' : 'negative';
      const sourceSelected = direction === 'toPositive' ? negativeSelected : positiveSelected;

      if (sourceSelected.size === 0) return;

      setLocalPlaybookConfluences((prev) =>
        prev.map((link) => {
          if (sourceSelected.has(link.confluenceId)) {
            return { ...link, polarity: targetPolarity };
          }
          return link;
        })
      );

      // Clear all selection after move
      setPositiveSelected(new Set());
      setNegativeSelected(new Set());
    },
    [positiveSelected, negativeSelected, setLocalPlaybookConfluences]
  );

  /**
   * Handle unlinking a single confluence
   * Removes the confluence link from localPlaybookConfluences, marking the playbook dirty
   * Uses functional update to handle rapid sequential unlinks correctly
   * Preserves full PlaybookConfluenceLink objects (including polarity) for remaining items
   */
  const handleUnlink = useCallback(
    (confluenceId: string) => {
      setLocalPlaybookConfluences((prev) =>
        prev.filter((link) => link.confluenceId !== confluenceId)
      );
      // Also remove from selection if selected
      setPositiveSelected((prev) => {
        const newSet = new Set(prev);
        newSet.delete(confluenceId);
        return newSet;
      });
      setNegativeSelected((prev) => {
        const newSet = new Set(prev);
        newSet.delete(confluenceId);
        return newSet;
      });
    },
    [setLocalPlaybookConfluences]
  );

  /**
   * Handle add action - opens picker modal
   */
  const handleAdd = useCallback(() => {
    setIsPickerOpen(true);
  }, []);

  /**
   * Handle picker close
   */
  const handlePickerClose = useCallback(() => {
    setIsPickerOpen(false);
  }, []);

  /**
   * Handle picker selection change
   * Replaces localPlaybookConfluences with new selection from picker
   */
  const handlePickerSelectionChange = useCallback(
    (newConfluences: PlaybookConfluenceLink[]) => {
      setLocalPlaybookConfluences(newConfluences);
    },
    [setLocalPlaybookConfluences]
  );

  /**
   * Handle edit action from the linked list
   * Refreshes categories and opens the edit modal with the selected confluence
   */
  const handleEdit = useCallback(
    async (confluence: Confluence) => {
      // Refresh categories before opening modal to ensure fresh data
      await loadCategories();
      setEditingConfluence(confluence);
      setIsEditModalOpen(true);
    },
    [loadCategories]
  );

  /**
   * Handle closing the edit modal
   * Clears editingConfluence state
   */
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingConfluence(undefined);
  }, []);

  /**
   * Handle save from the edit modal
   * Updates the confluence via service and notifies sync
   * No manual data reload needed - LinkedConfluencesDualList's sync subscription handles refresh
   */
  const handleSave = useCallback(
    async (data: { name: string; description?: string; marketConditionCategoryId?: string }) => {
      if (!editingConfluence) return;

      setIsSaving(true);
      try {
        await confluenceService.update(editingConfluence.id, data);
        syncCoordinatorService.notifyLocalChange('confluences', editingConfluence.id);
        syncCoordinatorService.notifyChange('confluences', editingConfluence.id);
        handleCloseEditModal();
      } catch (error) {
        console.error('Failed to save confluence:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [editingConfluence, handleCloseEditModal]
  );

  /**
   * Handle opening the create modal
   * Clears any stale editing state, resets polarity to default, loads fresh categories, then opens modal
   */
  const handleOpenCreateModal = useCallback(async () => {
    // Clear any stale editing state to prevent data flash
    setEditingConfluence(undefined);
    // Reset polarity to default
    setCreatePolarity('negative');
    // Load fresh categories before opening modal
    await loadCategories();
    setIsCreateModalOpen(true);
  }, [loadCategories]);

  /**
   * Handle closing the create modal
   */
  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  /**
   * Handle polarity change in create mode
   */
  const handleCreatePolarityChange = useCallback((polarity: 'positive' | 'negative') => {
    setCreatePolarity(polarity);
  }, []);

  /**
   * Handle create from the create modal
   * Creates new confluence via service, notifies sync, auto-links to playbook with polarity, closes modal
   */
  const handleCreate = useCallback(
    async (data: { name: string; description?: string; marketConditionCategoryId?: string }) => {
      setIsSaving(true);
      try {
        // Build confluence data, only including optional fields if they have values
        // (required for exactOptionalPropertyTypes)
        const confluenceData: { name: string; description?: string; marketConditionCategoryId?: string } = {
          name: data.name,
        };
        if (data.description) {
          confluenceData.description = data.description;
        }
        if (data.marketConditionCategoryId) {
          confluenceData.marketConditionCategoryId = data.marketConditionCategoryId;
        }

        // Create the new confluence entity
        const newConfluence = await confluenceService.add(confluenceData);

        // Notify sync for same-tab and cross-tab updates
        syncCoordinatorService.notifyLocalChange('confluences', newConfluence.id);
        syncCoordinatorService.notifyChange('confluences', newConfluence.id);

        // Auto-link the new confluence to the current playbook with selected polarity
        setLocalPlaybookConfluences((prev) => [
          ...prev,
          { confluenceId: newConfluence.id, polarity: createPolarity },
        ]);

        handleCloseCreateModal();
      } catch (error) {
        console.error('Failed to create confluence:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [createPolarity, setLocalPlaybookConfluences, handleCloseCreateModal]
  );

  // Determine which modal is open and configure props accordingly
  const isModalOpen = isEditModalOpen || isCreateModalOpen;
  const modalConfluence = isCreateModalOpen ? undefined : editingConfluence;
  const handleModalClose = isCreateModalOpen ? handleCloseCreateModal : handleCloseEditModal;
  const handleModalSave = isCreateModalOpen ? handleCreate : handleSave;

  return (
    <>
      {/* Linked Confluences List with unlink, add existing, create new, and edit actions */}
      <LinkedConfluencesDualList
        confluences={localPlaybookConfluences}
        onUnlink={handleUnlink}
        onEdit={handleEdit}
        onAdd={handleAdd}
        onCreateNew={handleOpenCreateModal}
        positiveSelected={positiveSelected}
        negativeSelected={negativeSelected}
        onSelectionChange={handleSelectionChange}
        onMove={handleMove}
      />

      {/* Confluence Picker Modal */}
      <ConfluenceLinkPickerModal
        isOpen={isPickerOpen}
        onClose={handlePickerClose}
        selectedConfluences={localPlaybookConfluences}
        onSelectionChange={handlePickerSelectionChange}
      />

      {/* Confluence Modal - reused for both edit and create modes */}
      <ConfluenceModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        confluence={modalConfluence}
        categories={categories}
        allCategories={allCategories}
        onSave={handleModalSave}
        isSaving={isSaving}
        showPolaritySelector={isCreateModalOpen}
        selectedPolarity={createPolarity}
        onPolarityChange={handleCreatePolarityChange}
      />
    </>
  );
};
